import { Component, OnDestroy, OnInit, effect, inject, input, output, signal } from '@angular/core';
import { Organization } from '../../../organization-models';
import { UserCreateRequest, UserEditRequest, UserService } from '../../../../../shared/services/user.service';
import { Role, User, UserSearchEvent } from '../../../../users/user.model';
import { firstValueFrom, Observable, share, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Department } from '../../../../department/department.model';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { RouterLink } from '@angular/router';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AsyncPipe } from '@angular/common';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelect, MatOption } from '@angular/material/select';

@Component({
  selector: 'adm-step6-users',
  templateUrl: './step6-users.component.html',
  styleUrl: './step6-users.component.scss',
  imports: [
    CustomDropDownArrowDirective,
    RouterLink,
    FeedbackInfoComponent,
    AsyncPipe,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatSelect,
    MatOption,
  ],
})
export class Step6UsersComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private snackbarService = inject(SnackbarService);

  organization = input.required<Organization>();
  departments = input.required<Department[]>();

  skipStep = output<void>();

  public addingUsers = false;
  public users = signal<User[]>([]);
  public userBeingEdited: User | null = null;
  public roles$: Observable<Role[]>;
  public showForm = false;
  public displayColumns = ['name', 'email', 'role', 'departments', 'actions'];

  private subscription = new Subscription();

  public form: FormGroup<{
    email: FormControl<string>;
    name: FormControl<string>;
    role: FormControl<Role['name'] | null>;
    departments: FormControl<Department['id'][]>;
  }> = new FormGroup({
    email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    role: new FormControl<Role['name'] | null>(null, [Validators.required]),
    departments: new FormControl<Department['id'][]>(
      {
        disabled: false,
        value: [],
      },
      { nonNullable: true }
    ),
  });

  constructor() {
    effect(() => {
      const departments = this.departments();
      if (this.form) {
        if (departments.length === 0) {
          this.form.controls.departments.disable();
        } else {
          this.form.controls.departments.enable();
        }
      }
    });
  }
  async ngOnInit() {
    this.roles$ = this.userService.getRoles().pipe(share());
    this.users.set(await this.getUsers());
  }

  async ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public handleAddPortalUser() {
    this.showForm = true;
  }

  public resetAndHideForm() {
    this.form.reset();
    this.userBeingEdited = null;
    this.showForm = false;
    this.form.controls.email.enable();
  }

  public async submitForm() {
    const roles = await firstValueFrom(this.roles$);
    const role = roles.find(r => r.name === this.form.controls.role.value);
    if (!role) {
      this.snackbarService.error('Vælg venligst en gyldig rolle');
      return;
    }

    const [firstName, ...lastName] = this.form.controls.name.value.split(' ');

    const user: Partial<User> = {
      firstName,
      lastName: lastName.join(''),
      email: this.form.controls.email.value,
      roles: [role],
      organizationId: this.organization().id,
    };

    const departmentIds = this.form.controls.departments.value;
    const departments = this.departments();

    const userDepartments = (departmentIds ?? [])
      .map(id => departments.find(d => d.departmentId === id))
      .filter((department): department is Department => !!department);
    if (this.userBeingEdited) {
      await this.updateExistingUser(user, roles, userDepartments);
    } else {
      await this.createNewUser(user, userDepartments);
    }
  }

  public async updateExistingUser(user: Partial<User>, roles: Role[], departments: Department[]) {
    this.addingUsers = true;
    const originalUser = this.userBeingEdited;
    if (!originalUser) {
      this.addingUsers = false;
      return;
    }

    const selectedRoleName = user.roles?.[0]?.name;
    const otherRole = roles.find(r => r.name !== selectedRoleName);
    const editRequest: UserEditRequest = {
      id: originalUser.id,
      email: user.email ?? originalUser.email,
      firstName: user.firstName ?? originalUser.firstName,
      lastName: user.lastName ?? originalUser.lastName,
      roles: user.roles ?? [originalUser.role],
      rolesToRemove: otherRole ? [otherRole] : [],
    };

    try {
      await firstValueFrom(this.userService.edit(editRequest));
      const updatedUser = await firstValueFrom(this.userService.getUser(originalUser.id));
      await this.updateDepartmentRestrictions(updatedUser, departments);
      this.resetAndHideForm();
      this.users.set(await this.getUsers());
      this.addingUsers = false;
    } catch (err) {
      this.addingUsers = false;
      const errorCode = this.getErrorCode(err);
      this.snackbarService.error(`Der opstod en fejl under ændring af brugeren. Fejlkode: ${errorCode ?? 'ukendt'}`);
    }
  }

  public async createNewUser(user: Partial<User>, departments: Department[]) {
    this.addingUsers = true;
    try {
      const createRequest: UserCreateRequest = {
        organizationId: this.organization().id,
        email: user.email ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        role: user.roles?.[0] ?? ({} as Role),
      };

      const createdUserIdentifier = await firstValueFrom(this.userService.create(createRequest));
      const createdUser = await firstValueFrom(this.userService.getUser(createdUserIdentifier));
      if (departments.length) {
        await this.updateDepartmentRestrictions(createdUser, departments);
      }
      this.resetAndHideForm();
      this.users.set(await this.getUsers());
      this.addingUsers = false;
    } catch (err) {
      this.addingUsers = false;
      const errorCode = this.getErrorCode(err);
      if (errorCode === 100711) {
        this.snackbarService.error('Der findes allerede en bruger med den email');
      } else {
        this.snackbarService.error(`Der opstod en fejl under oprettelsen af brugeren. Fejlkode: ${errorCode ?? 'ukendt'}`);
      }
    }
  }

  public async updateDepartmentRestrictions(user: User, departments: Department[]) {
    try {
      await firstValueFrom(
        this.userService.restrictPortalUserDeparments({
          organizationId: this.organization().id,
          userId: user.id,
          departmentIds: departments.map(d => d.departmentId),
          departmentNames: departments.map(d => d.departmentName),
        })
      );
    } catch (err) {
      const errorCode = this.getErrorCode(err);
      this.snackbarService.error(
        `Der opstod en fejl i forbindelse med oprettelse af afdelingsrestriktioner. Fejlkode: ${errorCode ?? 'ukendt'}`
      );
    }
  }

  private getErrorCode(err: unknown): number | string | undefined {
    if (!err || typeof err !== 'object') {
      return undefined;
    }

    const maybeError = err as { error?: { code?: number | string } };
    return maybeError.error?.code;
  }

  public async editUser(user: User) {
    this.form.setValue({
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      role: user.role.name,
      departments: user.departmentRestrictionIds || [],
    });

    this.userBeingEdited = user;
    this.showForm = true;
    this.form.controls.email.disable();
  }

  private async getUsers(): Promise<User[]> {
    const searchEvent = new UserSearchEvent();
    searchEvent.organizationId = this.organization().id;
    const response = await firstValueFrom(this.userService.getUsers(searchEvent));

    const roles = await firstValueFrom(this.roles$);
    return response.items
      .map(user => {
        user.role = roles.find(r => r.name === user.rolesAsStrings[0]) ?? user.role;
        return user;
      })
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
  }
}
